import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    FaHome, FaTasks, FaCalendarAlt, FaFileAlt, FaUserShield, FaUsers,
    FaUserPlus, FaTicketAlt, FaBuilding, FaShieldAlt, FaClipboardList,
    FaFolderOpen, FaChartLine, FaClock, FaUserFriends, FaUserCheck, FaUserTie,
    FaHandshake, FaBook, FaCog
} from 'react-icons/fa';
import { authService } from '../../services/auth.service';
import api from '../../services/api';

const Sidebar = () => {
    const location = useLocation();
    const user = authService.getCurrentUser();
    const isAdmin = user?.role?.name === 'super_admin' || user?.role?.name === 'admin';
    const isManager = user?.role?.name?.includes('manager');

    const [hasAssignedClients, setHasAssignedClients] = useState(false);
    const [loadingClients, setLoadingClients] = useState(true);

    useEffect(() => {
        // Only check for non-admin users
        if (!isAdmin) {
            checkAssignedClients();
        } else {
            setLoadingClients(false);
        }
    }, [isAdmin]);

    const checkAssignedClients = async () => {
        try {
            const response = await api.get('/clients/my-clients');
            if (response.data.success && response.data.count > 0) {
                setHasAssignedClients(true);
            } else {
                setHasAssignedClients(false);
            }
        } catch (error) {
            // If 404 or any error, don't show the menu
            console.log('Client check:', error.response?.status || 'error');
            setHasAssignedClients(false);
        } finally {
            setLoadingClients(false);
        }
    };

    // Backend checks day
    const isFriday = () => {
        return new Date().getDay() === 5; // 5 = Friday
    };

    // Frontend shows status
    if (isFriday) {
        // Show all games
    } else {
        // Show "Come back Friday" message
    }

    const isActive = (path) => location.pathname === path;

    const menuItems = [
        { name: 'Dashboard', icon: FaHome, path: '/dashboard' },
    ];

    const adminItems = [
        { name: 'Admin Overview', icon: FaUserShield, path: '/dashboard/admin' },
        { name: 'Users', icon: FaUsers, path: '/dashboard/admin/users' },
        { name: 'Pending Approvals', icon: FaUserPlus, path: '/dashboard/admin/approvals' },
        { name: 'Invite Codes', icon: FaTicketAlt, path: '/dashboard/admin/invites' },
        { name: 'Departments', icon: FaBuilding, path: '/dashboard/admin/departments' },
        { name: 'Roles', icon: FaShieldAlt, path: '/dashboard/admin/roles' },
        { name: 'Manage Documents', icon: FaFolderOpen, path: '/dashboard/admin/documents' },
        { name: 'Manage Tasks', icon: FaClipboardList, path: '/dashboard/admin/tasks' },
        { name: 'Employee Leaves', icon: FaUserCheck, path: '/dashboard/admin/leaves' },
        { name: 'Manager Leaves', icon: FaUserTie, path: '/dashboard/admin/manager-leaves' },
        { name: 'All Attendance', icon: FaClock, path: '/dashboard/admin/attendance' },
        { name: 'Client Management', icon: FaHandshake, path: '/dashboard/admin/clients' },
        { name: 'Manage Blogs', icon: FaCog, path: '/dashboard/admin/manage-blogs' },
        { name: 'Blog Approvals', icon: FaFileAlt, path: '/dashboard/admin/blog-approvals' },
        { name: 'Employee Performance', icon: FaFileAlt, path: '/dashboard/admin/EmployeePerformance' },
        //{
        //    name: 'Freelancer Projects',
        //    path: '/dashboard/admin/FreelancerHub',
        //    icon: '💼' // or your icon component
        //},
        //{
        //    name: 'Permissions',
        //    path: '/dashboard/admin/PermissionManagement',
        //    icon: '🔐', // or your icon component
        //    adminOnly: true
        //}
    ];

    const managerItems = [
        { name: 'Manager Dashboard', icon: FaChartLine, path: '/dashboard/manager' },
        { name: 'My Team', icon: FaUserFriends, path: '/dashboard/manager/team' },
        { name: 'Team Tasks', icon: FaTasks, path: '/dashboard/manager/tasks' },
        { name: 'Employee Leaves', icon: FaUserCheck, path: '/dashboard/manager/leaves' },
        { name: 'Team Attendance', icon: FaClock, path: '/dashboard/manager/attendance' },
    ];

    const employeeItems = [
        { name: 'My Attendance', icon: FaClock, path: '/dashboard/attendance' },
        { name: 'My Tasks', icon: FaTasks, path: '/dashboard/tasks' },
        { name: 'My Leaves', icon: FaCalendarAlt, path: '/dashboard/leaves' },
        { name: 'My Documents', icon: FaFileAlt, path: '/dashboard/documents' },
        { name: 'My Blogs', icon: FaFileAlt, path: '/dashboard/blogs' },
        { name: 'Knowledge Hub', icon: FaBook, path: '/dashboard/all-blogs' },
        {
            name: 'Friday Fun Zone',
            path: '/dashboard/funzone',
            icon: '🎉',
            badge: isFriday ? 'ACTIVE' : null
        },
        {
            name: 'Support Forum',
            path: '/dashboard/forum',
            icon: '🤝'
        }
    ];

    return (
        <div className="w-64 bg-gray-900 text-white flex flex-col">
            <div className="p-6 border-b border-gray-800">
                <h1 className="text-2xl font-bold">PlatypusBox</h1>
                <p className="text-sm text-gray-400 mt-1">Employee Management</p>
            </div>

            <nav className="flex-1 overflow-y-auto">
                {/* Main Menu */}
                <div className="mb-2">
                    {menuItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center px-6 py-3 hover:bg-gray-800 transition-colors ${isActive(item.path) ? 'bg-gray-800 border-l-4 border-blue-500' : ''
                                }`}
                        >
                            <item.icon className="mr-3" />
                            {item.name}
                        </Link>
                    ))}
                </div>

                {/* Admin Menu */}
                {isAdmin && (
                    <div className="mb-2">
                        <div className="px-6 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-800">
                            Admin Panel
                        </div>
                        {adminItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center px-6 py-2 hover:bg-gray-800 transition-colors ${isActive(item.path) ? 'bg-gray-800 border-l-4 border-blue-500' : ''
                                    }`}
                            >
                                <item.icon className="mr-3 text-sm" />
                                <span className="text-sm">{item.name}</span>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Manager Menu */}
                {isManager && !isAdmin && (
                    <div className="mb-2">
                        <div className="px-6 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-800">
                            Manager Tools
                        </div>
                        {managerItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center px-6 py-2 hover:bg-gray-800 transition-colors ${isActive(item.path) ? 'bg-gray-800 border-l-4 border-green-500' : ''
                                    }`}
                            >
                                <item.icon className="mr-3 text-sm" />
                                <span className="text-sm">{item.name}</span>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Employee Menu */}
                <div className="mb-2">
                    <div className="px-6 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-800">
                        Employee
                    </div>
                    {employeeItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center px-6 py-3 hover:bg-gray-800 transition-colors ${isActive(item.path) ? 'bg-gray-800 border-l-4 border-purple-500' : ''
                                }`}
                        >
                            <item.icon className="mr-3" />
                            {item.name}
                        </Link>
                    ))}

                    {/* My Clients - Only show if: not admin AND has assigned clients AND not loading */}
                    {!isAdmin && !loadingClients && hasAssignedClients && (
                        <Link
                            to="/dashboard/clients"
                            className={`flex items-center px-6 py-3 hover:bg-gray-800 transition-colors ${isActive('/dashboard/clients') ? 'bg-gray-800 border-l-4 border-purple-500' : ''
                                }`}
                        >
                            <FaHandshake className="mr-3" />
                            My Clients
                        </Link>
                    )}
                </div>
            </nav>

            <div className="p-4 border-t border-gray-800">
                <p className="text-xs text-gray-500 text-center">Logged in as:</p>
                <p className="text-sm font-medium text-center mt-1 truncate" title={user?.name}>
                    {user?.name}
                </p>
                <p className="text-xs text-gray-600 text-center mt-1">{user?.role?.displayName}</p>
            </div>
        </div>
    );
};

export default Sidebar;